import {
  pickCampaign,
  resolveClickUrl,
  type HouseAdCampaign,
  type AdPlatform,
} from "@yjsgames/house-ads";
import { HOUSE_AD_CAMPAIGNS, houseAdAssetUrl } from "./houseAdData.js";
import type {
  RewardedAdProvider,
  ShowRewardedOptions,
  HouseAdModalRequest,
} from "@yjsgames/ad-ports";
import type { AnalyticsSink } from "@yjsgames/analytics-ports";
import { AnalyticsEvent } from "../analytics/analyticsEvents.js";

/**
 * 웹(자사) 하우스 광고 구현 — `RewardedAdProvider`(DIP)의 자사광고 구현체.
 * 서드파티 SDK 없이 캠페인 추첨 → 전체화면 오버레이 표시(주입 콜백) → analytics 발화. 닫기(10초 강제 후) 시 onReward.
 * 광고 표시(DOM 오버레이)는 모르고 ModalHost가 주입한 openModal/closeModal(HouseAdOverlay)에 위임한다.
 * 클릭 시 이동 URL은 `resolveClickUrl(campaign, platform)`로 기기별(iOS App Store / Android·웹 Play Store) 분기한다.
 *
 * 캠페인이 0건(전부 비활성)이면 광고 없이 즉시 onReward — 광고 코스트 상품의 보상이 막히지 않게 하는 운영 안전판.
 * (disable 실수와 의도적 안전판 구분 위해 `rewarded_ad_campaign_empty`를 발화한다.)
 */
export class HouseAdProvider implements RewardedAdProvider {
  constructor(
    private readonly analytics: AnalyticsSink,
    private readonly openModal: (req: HouseAdModalRequest<HouseAdCampaign>) => void,
    private readonly closeModal: () => void,
    private readonly campaigns: readonly HouseAdCampaign[] = HOUSE_AD_CAMPAIGNS,
    private readonly rng: () => number = Math.random,
    /** 클릭 URL 분기용 플랫폼 — 앱이 Capacitor.getPlatform()으로 주입(패키지는 문자열만 사용, DIP). */
    private readonly platform: AdPlatform = "web",
  ) {}

  showRewarded(opts: ShowRewardedOptions): void {
    const campaign = pickCampaign(this.rng, this.campaigns);
    if (!campaign) {
      // 활성 캠페인 0 → 광고 없이 보상 (운영 안전판). disable 실수 진단용 이벤트 발화.
      this.analytics.log(AnalyticsEvent.RewardedAdCampaignEmpty);
      opts.onReward();
      return;
    }
    this.analytics.log(AnalyticsEvent.HouseAdImpression, {
      campaign_id: campaign.campaignId,
      ad_type: campaign.adType,
    });
    this.openModal({
      campaign,
      mediaUrl: houseAdAssetUrl(campaign),
      format: "reward",
      // reward: 10초 강제 시청 후 닫기 = 시청 완료 = 보상 지급.
      onClose: () => {
        this.analytics.log(AnalyticsEvent.RewardedAdComplete, {
          provider: "house",
          campaign_id: campaign.campaignId,
        });
        this.closeModal();
        opts.onReward();
      },
      onCtaClick: () => {
        const url = resolveClickUrl(campaign, this.platform);
        this.analytics.log(AnalyticsEvent.HouseAdClick, {
          campaign_id: campaign.campaignId,
          url,
        });
        this.openUrl(url);
      },
    });
  }

  /** 외부(자사 게임) URL 열기 — 사용자 클릭(제스처) 내 호출이라 itch.io iframe에서도 대부분 통과. */
  private openUrl(url: string): void {
    if (typeof window !== "undefined") {
      window.open(url, "_blank", "noopener");
    }
  }
}
